package com.klantenstop.web.components.search

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.klantenstop.web.components.CustomSelectMenuStyle
import com.klantenstop.web.components.CustomSelectOptionStyle
import com.klantenstop.web.components.DownwardCustomSelectMenuVariant
import com.klantenstop.web.components.SelectedCustomSelectOptionVariant
import com.klantenstop.web.components.UpwardCustomSelectMenuVariant
import com.klantenstop.web.data.service.CitySuggestion
import com.materialkobweb.components.widgets.DsEditableField
import com.materialkobweb.styles.MaterialColorVars
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.AutoComplete
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

private const val CITY_MENU_HEIGHT_ESTIMATE = 270

/**
 * Accessible city autocomplete input with WAI-ARIA combobox/listbox pattern.
 *
 * Features:
 *  - Full keyboard navigation (↓/↑ navigate options; ↑ from first option returns to input; Enter selects; Escape dismisses)
 *  - WAI-ARIA combobox + listbox attributes set imperatively on the underlying `<input>` element
 *  - Automatic up/down placement based on viewport space
 *  - Dismiss on outside click
 *  - Focus always returns to input after selection or Escape
 *
 * @param id HTML id applied to the underlying `<input>` element via [DsEditableField]
 * @param label Label text shown above the field
 * @param placeholder Placeholder shown inside the input
 * @param value Current text value
 * @param suggestions Suggestions list — non-empty means the dropdown is open
 * @param onValueChange Called as the user types
 * @param onSuggestionSelect Called when a suggestion is chosen (click or Enter)
 * @param onDismiss Called when the suggestions should be dismissed, such as on Escape,
 * outside click, or when Tab/Shift+Tab is pressed while the list is open
 * @param modifier Applied to the outer container
 * @param valid Whether the field is in a valid state (passed to [DsEditableField])
 */
@Composable
fun CityAutocomplete(
    id: String,
    label: String,
    placeholder: String = "",
    value: String,
    suggestions: List<CitySuggestion>,
    onValueChange: (String) -> Unit,
    onSuggestionSelect: (CitySuggestion) -> Unit,
    onDismiss: () -> Unit = {},
    modifier: Modifier = Modifier,
    valid: Boolean = true,
) {
    val containerId = "${id}-autocomplete"
    val isOpen = suggestions.isNotEmpty()
    var focusedIndex by remember { mutableStateOf<Int?>(null) }
    var openUpward by remember { mutableStateOf(false) }

    // Keep latest callback references for long-lived event handlers
    val latestSuggestions by rememberUpdatedState(suggestions)
    val latestOnSuggestionSelect by rememberUpdatedState(onSuggestionSelect)
    val latestOnDismiss by rememberUpdatedState(onDismiss)

    // Reset focus index whenever new suggestions arrive (prevents dangling index)
    LaunchedEffect(suggestions) { focusedIndex = null }

    // Set WAI-ARIA combobox attributes on the underlying <input> element imperatively
    LaunchedEffect(isOpen, focusedIndex) {
        (document.getElementById(id) as? HTMLElement)?.apply {
            setAttribute("role", "combobox")
            setAttribute("aria-autocomplete", "list")
            setAttribute("aria-haspopup", "listbox")
            setAttribute("aria-expanded", isOpen.toString())
            if (isOpen) {
                setAttribute("aria-controls", "${id}-listbox")
                if (focusedIndex != null) {
                    setAttribute("aria-activedescendant", "${id}-option-${focusedIndex}")
                } else {
                    removeAttribute("aria-activedescendant")
                }
            } else {
                removeAttribute("aria-controls")
                removeAttribute("aria-activedescendant")
            }
        }
    }

    // Move DOM focus to the highlighted option when keyboard navigates into the list
    LaunchedEffect(focusedIndex) {
        if (isOpen && focusedIndex != null) {
            (document.getElementById("${id}-option-${focusedIndex}") as? HTMLElement)?.focus()
        }
    }

    // Determine dropdown placement (up or down) when it first opens
    LaunchedEffect(isOpen) {
        if (isOpen) openUpward = shouldCitySelectOpenUpward(id)
    }

    // Dismiss on outside click
    DisposableEffect(isOpen) {
        if (!isOpen) return@DisposableEffect onDispose {}
        val handler: (Event) -> Unit = h@{ event ->
            val clicked = event.target as? HTMLElement
            val container = document.getElementById(containerId) as? HTMLElement
            if (container != null && clicked != null && !container.contains(clicked)) {
                latestOnDismiss()
                focusedIndex = null
            }
        }
        document.addEventListener("click", handler, true)
        onDispose { document.removeEventListener("click", handler, true) }
    }

    // Keyboard navigation — WAI-ARIA combobox/listbox interaction pattern:
    //   ↓        Navigate to next option (open list if closed). Wraps around.
    //   ↑        Navigate to previous option. From first option → returns focus to <input>.
    //   Enter    Select the currently focused option.
    //   Escape   Dismiss suggestions, return focus to <input>.
    //   Tab      Close list and let browser move focus to next/previous field naturally.
    //            Never call preventDefault — Shift+Tab must also work unobstructed.
    DisposableEffect(containerId) {
        val container = document.getElementById(containerId) as? HTMLElement
            ?: return@DisposableEffect onDispose {}

        val handler: (Event) -> Unit = h@{ event ->
            val key = event.asDynamic().key as? String ?: return@h
            val currentSuggestions = latestSuggestions  // always fresh via rememberUpdatedState
            val itemCount = currentSuggestions.size
            val open = itemCount > 0

            when (key) {
                "ArrowDown", "ArrowUp" -> {
                    if (itemCount == 0) return@h
                    event.preventDefault()
                    val current = focusedIndex  // reads current Compose state via delegate
                    // ArrowUp from no selection or from first option → return focus to input
                    if (key == "ArrowUp" && (current == null || current == 0)) {
                        focusedIndex = null
                        (document.getElementById(id) as? HTMLElement)?.focus()
                        return@h
                    }
                    focusedIndex = when {
                        current == null -> if (key == "ArrowDown") 0 else itemCount - 1
                        key == "ArrowDown" -> (current + 1) % itemCount
                        else -> current - 1
                    }
                }

                "Enter" -> {
                    val idx = focusedIndex ?: return@h
                    event.preventDefault()
                    currentSuggestions.getOrNull(idx)?.let { suggestion ->
                        latestOnSuggestionSelect(suggestion)
                    }
                    focusedIndex = null
                    (document.getElementById(id) as? HTMLElement)?.focus()
                }

                "Escape" -> {
                    if (!open) return@h
                    event.preventDefault()
                    latestOnDismiss()
                    focusedIndex = null
                    (document.getElementById(id) as? HTMLElement)?.focus()
                }

                "Tab" -> {
                    // WAI-ARIA combobox pattern: Tab (and Shift+Tab) always moves focus out naturally.
                    // Never call preventDefault — just close the list and let the browser handle focus.
                    if (!open) return@h
                    latestOnDismiss()
                    focusedIndex = null
                }
            }
        }

        container.addEventListener("keydown", handler)
        onDispose { container.removeEventListener("keydown", handler) }
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .position(Position.Relative)
            .then(modifier)
            .attrsModifier { id(containerId) }
    ) {
        DsEditableField(
            modifier = Modifier.fillMaxWidth(),
            id = id,
            label = label,
            placeholder = placeholder,
            value = value,
            backgroundColor = MaterialColorVars.SurfaceContainer.value(),
            autoComplete = AutoComplete.off,
            valid = valid,
            onValueChange = onValueChange
        )

        if (isOpen) {
            Column(
                modifier = CustomSelectMenuStyle.toModifier(
                    if (openUpward) UpwardCustomSelectMenuVariant else DownwardCustomSelectMenuVariant
                ).attrsModifier {
                    id("${id}-listbox")
                    attr("role", "listbox")
                    attr("aria-label", label)
                }
            ) {
                suggestions.forEachIndexed { index, suggestion ->
                    val isHighlighted = index == focusedIndex
                    Row(
                        modifier = CustomSelectOptionStyle
                            .toModifier(if (isHighlighted) SelectedCustomSelectOptionVariant else null)
                            .attrsModifier {
                                id("${id}-option-${index}")
                                tabIndex(-1)
                                attr("role", "option")
                                attr("aria-selected", isHighlighted.toString())
                            }
                            .onClick {
                                latestOnSuggestionSelect(suggestion)
                                focusedIndex = null
                                (document.getElementById(id) as? HTMLElement)?.focus()
                            },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            modifier = Modifier
                                .color(MaterialColorVars.OnSurface.value())
                                .padding(left = 8.px),
                            text = suggestion.displayName
                        )
                    }
                }
            }
        }
    }
}

private fun shouldCitySelectOpenUpward(inputId: String): Boolean {
    val el = document.getElementById(inputId) as? HTMLElement ?: return false
    return (window.innerHeight - el.getBoundingClientRect().bottom) < CITY_MENU_HEIGHT_ESTIMATE
}



